import React from "react";

/**
 * Modal component for adding/managing species images
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {Function} onClose - Callback to close the modal
 * @param {Array} specieImageData - Existing images data [{url, filename, image_id, species_id}]
 * @param {Array} imageFiles - Current image files state
 * @param {Function} setImageFiles - Setter for image files
 * @param {Function} handleImageChange - Handler for image file input change
 * @param {Function} handleAddImageInput - Handler to add another file input
 * @param {Function} handleRemoveImageInput - Handler to remove a file input
 * @param {Function} handleDeleteImage - Handler to delete existing image
 * @param {Function} handleAddSpeciePictures - Handler to upload new images
 */
export default function AddImageModal({
    isOpen,
    onClose,
    specieImageData,
    imageFiles,
    setImageFiles,
    handleImageChange,
    handleAddImageInput,
    handleRemoveImageInput,
    handleDeleteImage,
    handleAddSpeciePictures
}) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200">
                    <h3 className="font-heading text-2xl font-bold text-charcoal">Lägg till bilder</h3>
                </div>
                
                <div className="p-6">
                    {/* Show existing images first */}
                    {specieImageData && specieImageData.length > 0 && (
                        <div className="mb-6">
                            <h4 className="text-sm font-semibold text-charcoal mb-3">Befintliga bilder:</h4>
                            <div className="space-y-2">
                                {specieImageData.map((imageData, index) => (
                                    <div key={`existing-${index}`} className="flex items-center gap-3 bg-green-50 p-3 rounded-lg border border-green-200">
                                        <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span className="text-sm text-green-800 font-medium truncate flex-1">
                                            {imageData.filename}
                                        </span>
                                        <button
                                            onClick={() => {
                                                handleDeleteImage(imageData);
                                                if (specieImageData.length === 1) {
                                                    onClose();
                                                }
                                            }}
                                            className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded transition-colors"
                                            title="Ta bort denna bild"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-sm font-semibold text-charcoal mb-3">Lägg till nya bilder:</p>
                            </div>
                        </div>
                    )}
                    
                    <div className="space-y-4">
                        {imageFiles.map((file, index) => (
                            <div key={index} className="flex items-center gap-4">
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(event) => handleImageChange(event, index)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-forest file:text-white hover:file:bg-leaf file:cursor-pointer"
                                    />
                                    {file && file instanceof File && (
                                        <p className="text-xs text-gray-600 mt-1 truncate">
                                            {file.name}
                                        </p>
                                    )}
                                </div>
                                {imageFiles.length > 1 && (
                                    <button
                                        onClick={() => handleRemoveImageInput(index)}
                                        className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors duration-200 flex-shrink-0"
                                        title="Ta bort"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        ))}
                        
                        <button
                            onClick={handleAddImageInput}
                            className="w-full bg-gray-200 hover:bg-gray-300 text-charcoal font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
                        >
                            + Lägg till fler bilder
                        </button>
                    </div>
                </div>
                
                <div className="p-6 border-t border-gray-200 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-charcoal font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
                    >
                        Avbryt
                    </button>
                    <button
                        onClick={handleAddSpeciePictures}
                        className="flex-1 bg-forest hover:bg-leaf text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-300"
                    >
                        Spara bilder
                    </button>
                </div>
            </div>
        </div>
    );
}
